import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteBranding } from '../context/SiteBrandingContext';
import { DEFAULT_BRAND_FAVICON, type SiteBranding } from '../utils/siteBranding';

export type TcardsBrandLogoProps = {
  branding?: Partial<SiteBranding> | null;
  size?: number;
  showText?: boolean;
  tagline?: string;
  taglineClassName?: string;
  href?: string;
  /** In-app route (preferred for Mini Website nav — avoids full reload). */
  to?: string;
  className?: string;
  style?: React.CSSProperties;
  titleStyle?: React.CSSProperties;
  taglineStyle?: React.CSSProperties;
};

export const TcardsBrandLogo: React.FC<TcardsBrandLogoProps> = ({
  branding: brandingProp,
  size = 40,
  showText = true,
  tagline,
  taglineClassName,
  href,
  to,
  className,
  style,
  titleStyle,
  taglineStyle,
}) => {
  const { branding: ctxBranding } = useSiteBranding();
  const branding = { ...ctxBranding, ...(brandingProp || {}) };
  const siteName = branding.siteName || 'Tcards';
  const logoUrl = String(branding.logoUrl || '').trim();
  const iconSrc = logoUrl || DEFAULT_BRAND_FAVICON;

  const iconBox = (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <img src={iconSrc} alt={siteName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    </div>
  );

  const content = (
    <>
      {iconBox}
      {showText && (
        <div className="mwl-brand-text" style={{ minWidth: 0 }}>
          <div
            className="mwl-brand-title"
            style={{
              fontWeight: 800,
              fontSize: Math.max(15, Math.round(size * 0.45)),
              color: '#111827',
              whiteSpace: 'nowrap',
              ...titleStyle,
            }}
          >
            {siteName}
          </div>
          {tagline ? (
            <div className={taglineClassName} style={{ fontSize: 13, color: '#6B7280', ...taglineStyle }}>
              {tagline}
            </div>
          ) : null}
        </div>
      )}
    </>
  );

  const rootStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    color: 'inherit',
    minWidth: 0,
    cursor: href || to ? 'pointer' : undefined,
    ...style,
  };

  if (to) {
    return (
      <Link to={to} className={className} style={rootStyle} aria-label={siteName}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} className={className} style={rootStyle} aria-label={siteName}>
        {content}
      </a>
    );
  }

  return (
    <div className={className} style={rootStyle}>
      {content}
    </div>
  );
};

export default TcardsBrandLogo;
